import React, { useState, useEffect, useMemo, useContext } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import AdminService from '../../services/AdminService';
import { UiContext } from '../../context/UiContext';
import Stars from '../../modals/FeedbackModal/Stars';

import classes from './styles.module.scss';
import { getDate } from '../../helpers/getTimeline';
import Button from '../AutoButton';
import RadioButton from '../Form/RadioButton';
import Textarea from '../Form/Textarea';
import SegmentedControl from '../SegmentedControl';
import Switch from '../GradientSwitch';
import Tooltip from '../Tooltip';
import InfoAsset from '../../assets/images/modals/alert-circle.svg';
import RatingInfoAsset from '../../assets/images/modals/ratingtt.svg';
import { useUserStore } from '../../store';

export default function FeedbackContent({
  candidate,
  interview,
  job,
  refetch,
  existingFeedback = null,
  isReviewing = false,
  onClose,
  readOnly = false,
  showHeader = true,
  showCloseButton = false,
  className = '',
}) {
  console.log('interview', interview);
  console.log('existingFeedback', existingFeedback);
  const [isEditing, setIsEditing] = useState(false);
  const [feedbackComplexType, setFeedbackComplexType] = useState('General');
  const { showModal, showUnknownErrorModal, showNotification, setIsFetching } =
    useContext(UiContext);
  const isClient = useUserStore((state) => state.isClient);
  const isInterviewer = useUserStore((state) => state.isInterviewer);

  let feedbackType = 'Internal';
  if (isClient) {
    feedbackType = 'Client';
  }
  if (isInterviewer) {
    feedbackType = 'External';
  }

  const { t } = useTranslation();

  const isEditable = existingFeedback !== null && !isReviewing && !readOnly;

  function handleCloseFeedback() {
    if (onClose) {
      onClose();
    }
  }

  const feedbackComplexTypeOptions = [
    {
      value: 'General',
      label: t('common.FeedbackComplexTypeOptions.General'),
    },
    {
      value: 'Technical',
      label: t('common.FeedbackComplexTypeOptions.Technical'),
    },
    {
      value: 'Engagement',
      label: t('common.FeedbackComplexTypeOptions.Engagement'),
    },
  ];

  // Complex feedback items configuration
  const complexItemsConfig = useMemo(
    () => [
      // General & Behavioral Fit
      {
        tab: 'General',
        itemType: 'GEN_EDUCATION',
        titleKey: 'modals.FeedbackModal.general.educationalBackground.title',
        textKey: 'modals.FeedbackModal.general.educationalBackground.text',
      },
      {
        tab: 'General',
        itemType: 'GEN_WORK_EXPERIENCE',
        titleKey: 'modals.FeedbackModal.general.priorWorkExperience.title',
        textKey: 'modals.FeedbackModal.general.priorWorkExperience.text',
      },
      {
        tab: 'General',
        itemType: 'GEN_COMMUNICATION',
        titleKey: 'modals.FeedbackModal.general.communicationSkills.title',
        textKey: 'modals.FeedbackModal.general.communicationSkills.text',
      },
      {
        tab: 'General',
        itemType: 'GEN_TEAMWORK',
        titleKey:
          'modals.FeedbackModal.general.teamworkInterpersonalSkills.title',
        textKey:
          'modals.FeedbackModal.general.teamworkInterpersonalSkills.text',
      },
      {
        tab: 'General',
        itemType: 'GEN_INITIATIVE',
        titleKey: 'modals.FeedbackModal.general.initiativeMotivation.title',
        textKey: 'modals.FeedbackModal.general.initiativeMotivation.text',
      },

      // Technical Competence
      {
        tab: 'Technical',
        itemType: 'TECH_CORE_SKILLS',
        titleKey: 'modals.FeedbackModal.technical.coreJobSkills.title',
        textKey: 'modals.FeedbackModal.technical.coreJobSkills.text',
      },
      {
        tab: 'Technical',
        itemType: 'TECH_PROBLEM_SOLVING',
        titleKey:
          'modals.FeedbackModal.technical.problemSolvingAnalyticalAbility.title',
        textKey:
          'modals.FeedbackModal.technical.problemSolvingAnalyticalAbility.text',
      },
      {
        tab: 'Technical',
        itemType: 'TECH_TOOLS_CLOUD',
        titleKey:
          'modals.FeedbackModal.technical.toolsFrameworksCloudKnowledge.title',
        textKey:
          'modals.FeedbackModal.technical.toolsFrameworksCloudKnowledge.text',
      },
      {
        tab: 'Technical',
        itemType: 'TECH_DB_API',
        titleKey: 'modals.FeedbackModal.technical.databaseApiKnowledge.title',
        textKey: 'modals.FeedbackModal.technical.databaseApiKnowledge.text',
      },

      // Engagement & Culture Fit
      {
        tab: 'Engagement',
        itemType: 'ENG_INTEREST',
        titleKey:
          'modals.FeedbackModal.engagement.interestInRoleOrganization.title',
        textKey:
          'modals.FeedbackModal.engagement.interestInRoleOrganization.text',
      },
      {
        tab: 'Engagement',
        itemType: 'ENG_ORG_KNOWLEDGE',
        titleKey:
          'modals.FeedbackModal.engagement.knowledgeOfOrganization.title',
        textKey: 'modals.FeedbackModal.engagement.knowledgeOfOrganization.text',
      },
      {
        tab: 'Engagement',
        itemType: 'ENG_CLIENT_ORIENTATION',
        titleKey:
          'modals.FeedbackModal.engagement.clientCustomerOrientation.title',
        textKey:
          'modals.FeedbackModal.engagement.clientCustomerOrientation.text',
      },
      {
        tab: 'Engagement',
        itemType: 'ENG_TIME_MANAGEMENT',
        titleKey:
          'modals.FeedbackModal.engagement.timeManagementReliability.title',
        textKey:
          'modals.FeedbackModal.engagement.timeManagementReliability.text',
      },
    ],
    []
  );

  // Helper maps
  const itemTypeToTab = useMemo(() => {
    const map = {};
    complexItemsConfig.forEach((cfg) => {
      map[cfg.itemType] = cfg.tab;
    });
    return map;
  }, [complexItemsConfig]);

  // Build default feedback items for all configured questions
  const buildDefaultItems = () =>
    complexItemsConfig.map((cfg) => ({
      itemType: cfg.itemType,
      isApplicable: true,
      rating: 0,
      note: '',
    }));

  // Build initial feedback items from existing feedback or defaults
  const buildInitialItems = () => {
    if (existingFeedback === null) {
      return buildDefaultItems();
    }
    const existingItems = existingFeedback?.feedbackItems || [];
    return complexItemsConfig.map((cfg) => {
      const matched = existingItems.find(
        (it) => it.itemType === cfg.itemType || it.ItemType === cfg.itemType
      );
      return {
        itemType: cfg.itemType,
        isApplicable: matched?.isApplicable ?? matched?.IsApplicable ?? true,
        rating: matched?.rating ?? matched?.Rating ?? 0,
        note: matched?.note ?? matched?.Note ?? '',
      };
    });
  };

  const isAllTabsValid = (values) => {
    const items = values.feedbackItems || [];
    if (!items.length) return true;
    const allRatedOrNA = items.every(
      (i) => !i.isApplicable || (typeof i.rating === 'number' && i.rating > 0)
    );
    const notesOk = items.every(
      (i) =>
        !i.isApplicable ||
        i.rating === null ||
        i.rating >= 4 ||
        (i.note && i.note.trim().length > 0)
    );
    return allRatedOrNA && notesOk;
  };

  const formik = useFormik({
    initialValues: {
      feedbackText:
        existingFeedback !== null ? existingFeedback?.feedbackNote || '' : '',
      strengthNote:
        existingFeedback !== null ? existingFeedback?.strengthNote || '' : '',
      improvementNote:
        existingFeedback !== null
          ? existingFeedback?.improvementNote || ''
          : '',
      stars:
        existingFeedback !== null ? existingFeedback?.feedbackRating || 0 : 0,
      recommendationType:
        existingFeedback !== null
          ? existingFeedback?.recommendationType ?? null
          : null,
      feedbackItems: buildInitialItems(),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      const stars = values?.stars ?? 0;

      const isStrengthRequired = typeof stars === 'number' && stars >= 4;
      const isImprovementRequired =
        typeof stars === 'number' && stars > 0 && stars < 4;

      if (
        isStrengthRequired &&
        (!values.strengthNote || values.strengthNote.trim().length === 0)
      ) {
        errors.strengthNote = t('common.requiredField');
      }

      if (
        isImprovementRequired &&
        (!values.improvementNote || values.improvementNote.trim().length === 0)
      ) {
        errors.improvementNote = t('common.requiredField');
      }

      return errors;
    },
    onSubmit: async (values) => {
      const needsStrength = values.stars >= 4;
      const needsImprovement = values.stars > 0 && values.stars < 4;
      const strengthMissing =
        needsStrength &&
        (!values.strengthNote || values.strengthNote.trim().length === 0);
      const improvementMissing =
        needsImprovement &&
        (!values.improvementNote || values.improvementNote.trim().length === 0);

      // Skip complex section validation when contactType is Client
      const complexSectionInvalid =
        interview?.contactType !== 'Client' && !isAllTabsValid(values);

      const invalidSubmit =
        values.stars === 0 ||
        values.recommendationType === null ||
        complexSectionInvalid ||
        strengthMissing ||
        improvementMissing;

      if (invalidSubmit) {
        return;
      }

      try {
        setIsFetching(true);

        const payloadFeedbackItems = values.feedbackItems.map((item) => ({
          itemType: item.itemType,
          isApplicable: item.isApplicable,
          rating: item.isApplicable ? item.rating : null,
          note: item.isApplicable ? item.note : null,
        }));

        if (existingFeedback !== null) {
          await AdminService.updateFeedbackToInterview({
            jobApplicationId: candidate?.id ?? candidate?.jobApplicationId,
            interviewId: interview.id,
            feedbackId: existingFeedback.id,
            feedbackNote: values.feedbackText,
            feedbackRating: values.stars,
            recommendationType: values.recommendationType,
            strengthNote: values.strengthNote,
            improvementNote: values.improvementNote,
            feedbackItems: payloadFeedbackItems,
            feedbackType,
          });
        } else {
          await AdminService.addFeedbackToInterview({
            jobApplicationId: candidate?.id ?? candidate?.jobApplicationId,
            interviewId: interview.id,
            feedbackNote: values.feedbackText,
            feedbackRating: values.stars,
            recommendationType: values.recommendationType,
            strengthNote: values.strengthNote,
            improvementNote: values.improvementNote,
            feedbackItems: payloadFeedbackItems,
            feedbackType,
          });
        }

        if (refetch) {
          await refetch();
        }

        showNotification({
          text: t(
            isEditable && isEditing
              ? 'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Interview feedback updated successfully'
              : 'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Interview feedback submitted successfully'
          ),
        });
      } catch (error) {
        showUnknownErrorModal();
      } finally {
        handleCloseFeedback();
        setIsFetching(false);
      }
    },
  });

  const validationSubmit = (() => {
    const { stars, recommendationType, strengthNote, improvementNote } =
      formik.values;
    const needsStrength = stars >= 4;
    const needsImprovement = stars > 0 && stars < 4;
    const strengthMissing =
      needsStrength && (!strengthNote || strengthNote.trim().length === 0);
    const improvementMissing =
      needsImprovement &&
      (!improvementNote || improvementNote.trim().length === 0);

    // Skip complex section validation when contactType is Client
    const complexSectionInvalid =
      interview?.contactType !== 'Client' && !isAllTabsValid(formik.values);

    return (
      stars === 0 ||
      recommendationType === null ||
      complexSectionInvalid ||
      strengthMissing ||
      improvementMissing
    );
  })();

  const clearFeedback = () => {
    formik.resetForm({
      values: {
        feedbackText: '',
        stars: 0,
        recommendationType: null,
        strengthNote: '',
        improvementNote: '',
        feedbackItems: buildDefaultItems(),
      },
    });
  };

  useEffect(() => {
    return () => {
      formik.resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteFeedback = () => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Delete Feedback'
      ),
      text: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Are you sure you want to delete?'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.deleteFeedbackFromInterview({
            jobApplicationId: candidate?.id,
            interviewId: interview?.id,
            feedbackId: existingFeedback.id,
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Interview feedback deleted successfully'
            ),
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          handleCloseFeedback();
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const finalizeFeedback = () => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.finalizeFeedback'
      ),
      text: t(
        'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.areYouSureYouWantToFinalize'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.finalizeFeedbackFromInterview({
            jobApplicationId: candidate?.id,
            interviewId: interview?.id,
            feedbackId: existingFeedback.id,
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.interviewFeedbackFinalizedSuccessfully'
            ),
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          handleCloseFeedback();
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const setItemField = (itemType, field, value) => {
    const base = formik.values.feedbackItems || buildDefaultItems();
    const next = [...base];
    const idx = next.findIndex((i) => i.itemType === itemType);
    if (idx !== -1) {
      next[idx] = { ...next[idx], [field]: value };
      formik.setFieldValue('feedbackItems', next, false);
    }
  };

  const toggleItemNA = (itemType) => {
    const list = formik.values.feedbackItems || buildDefaultItems();
    const item = list.find((i) => i.itemType === itemType);
    if (!item) return;
    setItemField(itemType, 'isApplicable', !item.isApplicable);
  };

  const areAllItemsNAInTab = (tab) => {
    const items = (formik.values.feedbackItems || []).filter(
      (i) => itemTypeToTab[i.itemType] === tab
    );
    if (items.length === 0) return false;
    return items.every((i) => i.isApplicable === false);
  };

  const toggleSectionNA = (tab) => {
    const base = formik.values.feedbackItems || buildDefaultItems();
    const next = base.map((i) => {
      if (itemTypeToTab[i.itemType] === tab) {
        const shouldSetNA = !areAllItemsNAInTab(tab);
        return { ...i, isApplicable: !shouldSetNA };
      }
      return i;
    });
    formik.setFieldValue('feedbackItems', next, false);
  };

  const renderTabItems = (tab) => {
    const tabItems = complexItemsConfig.filter((cfg) => cfg.tab === tab);
    const list = formik.values.feedbackItems || buildDefaultItems();
    return tabItems.map((cfg) => {
      const item = list.find((i) => i.itemType === cfg.itemType);
      const isNA = item ? !item.isApplicable : false;
      return (
        <div className={classes.complexSectionContentItem} key={cfg.itemType}>
          <div className={classes.complexSectionContentItemLeft}>
            <div className={classes.complexSectionContentItemHeader}>
              <p className={classes.complexSectionContentItemHeaderTitleText}>
                {t(cfg.titleKey)}
              </p>
              <Switch
                label={t('common.itemNATitle')}
                isEnabled={isNA}
                readOnly={
                  existingFeedback !== null &&
                  (!isEditing || isReviewing || readOnly)
                }
                onClick={() => toggleItemNA(cfg.itemType)}
              />
            </div>
            {t(cfg.textKey) ? (
              <p className={classes.complexSectionContentItemBodyTitle}>
                {t(cfg.textKey)}
              </p>
            ) : null}
          </div>

          <div className={classes.complexSectionContentItemRight}>
            {isNA ? (
              <span className={classes.complexSectionContentItemNAText}>
                {t('common.itemNA')}
              </span>
            ) : (
              <>
                <Stars
                  readonly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  stars={item?.rating || 0}
                  setStars={(value) =>
                    setItemField(cfg.itemType, 'rating', value)
                  }
                  name={`rating-${cfg.itemType}`}
                  idPrefix={`rating-${cfg.itemType}`}
                />
                <Textarea
                  name={`note-${cfg.itemType}`}
                  height={80}
                  placeholder={t('common.feedbackPlaceholder')}
                  width="100%"
                  readonly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  onChange={(e) =>
                    setItemField(cfg.itemType, 'note', e.target.value)
                  }
                  onBlur={() => {}}
                  value={item?.note || ''}
                  error={
                    item?.rating > 0 &&
                    item?.rating < 4 &&
                    (!item?.note || item?.note.trim().length === 0)
                      ? t('common.requiredField')
                      : undefined
                  }
                  touched={item?.rating > 0 && item?.rating < 4}
                />
              </>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={classNames(classes.content, className)}>
      {showHeader && (
        <h2>
          {t(
            'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.interviewFeedback'
          )}
        </h2>
      )}
      {showCloseButton && onClose && (
        <div className={classes.close} onClick={handleCloseFeedback} />
      )}
      <div className={classes.info}>
        <div className={classes.container}>
          <p className={classes.positionInfo}>
            {t('common.candidate')}
            {': '}
            {`${candidate?.user?.userProfile?.firstName} ${candidate?.user?.userProfile?.lastName}`}{' '}
            | {t('common.position')}
            {': '}
            {job?.jobTitle}
          </p>
          {existingFeedback?.reviewedAt && readOnly ? (
            <p className={classes.review}>
              {`${t(
                'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.review'
              )} ${getDate(new Date(existingFeedback?.reviewedAt))}`}
            </p>
          ) : null}

          {(existingFeedback?.createdAt && isReviewing) ||
          (readOnly && !isReviewing && existingFeedback?.createdAt) ? (
            <p className={classes.review}>
              {`${t(
                'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.createdAt'
              )} ${getDate(new Date(existingFeedback?.createdAt))}`}
            </p>
          ) : null}
        </div>
        <div className={classes.container}>
          {isEditable && !isReviewing ? (
            <div className={classes.buttons}>
              <Button variant="delete" onClick={deleteFeedback} height={45}>
                {t('common.delete')}
              </Button>

              {isEditing ? (
                <Button
                  height={45}
                  disabled={validationSubmit}
                  onClick={() => formik.handleSubmit()}
                >
                  {t('common.save')}
                </Button>
              ) : (
                <Button
                  height={45}
                  variant="submit"
                  onClick={() => setIsEditing(true)}
                >
                  {t('common.edit')}
                </Button>
              )}
            </div>
          ) : null}

          {!isEditable && !isReviewing && !readOnly ? (
            <div className={classes.buttons}>
              <Button variant="blackWhite" onClick={clearFeedback} height={45}>
                {t('common.clear')}
              </Button>
              <Button
                height={45}
                disabled={validationSubmit}
                onClick={() => formik.handleSubmit()}
              >
                {t('common.submit')}
              </Button>
            </div>
          ) : null}

          {isReviewing && !readOnly && (
            <div className={classes.buttons}>
              <Button height={45} onClick={() => finalizeFeedback()}>
                {t('common.markFinal')}
              </Button>
            </div>
          )}
        </div>
      </div>
      <form
        onSubmit={formik.handleSubmit}
        className={classNames({
          [classes.noManipulations]:
            existingFeedback !== null &&
            (!isEditing || isReviewing || readOnly),
        })}
      >
        <div className={classes.formContent}>
          <div className={classes.cols}>
            <div className={classes.col}>
              <p className={classes.title}>
                {t(
                  'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.recommendCandidate'
                )}
              </p>
              <div className={classes.radios}>
                {interview?.contactType !== 'Client' && (
                  <RadioButton
                    readOnly={
                      existingFeedback !== null &&
                      (!isEditing || isReviewing || readOnly)
                    }
                    label={t(
                      'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.StrongHire'
                    )}
                    name="recommendationType"
                    isSelected={
                      formik.values.recommendationType === 'StrongHire'
                    }
                    onClick={() =>
                      formik.setFieldValue('recommendationType', 'StrongHire')
                    }
                    labelStyle={{
                      color: '#00e4ac',
                    }}
                  />
                )}
                <RadioButton
                  label={t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Hire'
                  )}
                  readOnly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  name="recommendationType"
                  isSelected={formik.values.recommendationType === 'Hire'}
                  onClick={() =>
                    formik.setFieldValue('recommendationType', 'Hire')
                  }
                  labelStyle={{
                    color:
                      interview?.contactType === 'Client'
                        ? '#00e4ac'
                        : '#242833',
                  }}
                />
                {interview?.contactType !== 'Client' && (
                  <RadioButton
                    readOnly={
                      existingFeedback !== null &&
                      (!isEditing || isReviewing || readOnly)
                    }
                    label={t(
                      'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Neutral'
                    )}
                    name="recommendationType"
                    isSelected={formik.values.recommendationType === 'Neutral'}
                    onClick={() =>
                      formik.setFieldValue('recommendationType', 'Neutral')
                    }
                    labelStyle={{
                      color: '#242833',
                    }}
                  />
                )}
                {interview?.contactType === 'Client' && (
                  <RadioButton
                    readOnly={
                      existingFeedback !== null &&
                      (!isEditing || isReviewing || readOnly)
                    }
                    label={t(
                      'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.Hold'
                    )}
                    name="recommendationType"
                    isSelected={formik.values.recommendationType === 'Neutral'}
                    onClick={() =>
                      formik.setFieldValue('recommendationType', 'Neutral')
                    }
                    labelStyle={{
                      color: '#242833',
                    }}
                  />
                )}
                <RadioButton
                  readOnly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  label={t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.NotHire'
                  )}
                  name="recommendationType"
                  isSelected={formik.values.recommendationType === 'NotHire'}
                  onClick={() =>
                    formik.setFieldValue('recommendationType', 'NotHire')
                  }
                  labelStyle={{
                    color: '#ff2d55',
                  }}
                />
              </div>
            </div>
            <div className={classNames(classes.col, classes.right)}>
              <p className={classes.title}>
                {t(
                  'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.rateCandidate'
                )}
                <Tooltip
                  fixed
                  text={<img src={RatingInfoAsset} alt="Info" />}
                  direction="bottom-left"
                >
                  <img src={InfoAsset} alt="Info" />
                </Tooltip>
              </p>
              <div className={classes.rating}>
                <Stars
                  readonly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  stars={formik.values.stars}
                  setStars={(value) => formik.setFieldValue('stars', value)}
                />
              </div>
            </div>
          </div>
          <div className={classes.colsContainer}>
            <div className={classes.cols}>
              <div className={classes.col}>
                <p className={classes.title}>
                  {t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.strengthNote'
                  )}
                </p>
                <Textarea
                  name="strengthNote"
                  height={84}
                  readonly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  touched={formik.touched.strengthNote}
                  error={formik.errors.strengthNote}
                  value={formik.values.strengthNote}
                  maxLength={5000}
                  style={{ backgroundColor: '#ffffff' }}
                  placeholder={t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.strengthNote.placeholder'
                  )}
                />
              </div>
              <div className={classes.col}>
                <p className={classes.title}>
                  {t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.improvementNote'
                  )}
                </p>
                <Textarea
                  name="improvementNote"
                  height={84}
                  readonly={
                    existingFeedback !== null &&
                    (!isEditing || isReviewing || readOnly)
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  touched={formik.touched.improvementNote}
                  error={formik.errors.improvementNote}
                  value={formik.values.improvementNote}
                  maxLength={5000}
                  style={{ backgroundColor: '#ffffff' }}
                  placeholder={t(
                    'dashboardComponents.Candidate.AssessmentAndInterview.Interview.Feedback.improvementNote.placeholder'
                  )}
                />
              </div>
            </div>
          </div>

          {interview?.contactType !== 'Client' && (
            <div className={classes.complexSection}>
              <div className={classes.complexSectionHeader}>
                <SegmentedControl
                  ariaLabel={t('modals.FeedbackModal.segmentAriaLabel')}
                  items={feedbackComplexTypeOptions}
                  selectedItem={feedbackComplexTypeOptions.find(
                    (i) => i.value === feedbackComplexType
                  )}
                  onChange={(item) => {
                    setFeedbackComplexType(item.value);
                  }}
                />
              </div>

              <div className={classes.complexSectionContent}>
                <div
                  className={classNames(classes.hidden, {
                    [classes.open]: feedbackComplexType === 'General',
                  })}
                >
                  <div className={classes.complexSectionHeader}>
                    <p className={classes.complexSectionContentItemTitle}>
                      {t('common.FeedbackComplexTypeOptions.General')}
                    </p>
                    <Switch
                      label={t('common.sectioNATitle')}
                      isEnabled={areAllItemsNAInTab('General')}
                      readOnly={
                        existingFeedback !== null &&
                        (!isEditing || isReviewing || readOnly)
                      }
                      onClick={() => toggleSectionNA('General')}
                    />
                  </div>
                  {areAllItemsNAInTab('General') ? (
                    <div className={classes.sectionNA}>
                      {t('common.sectionNA')}
                    </div>
                  ) : (
                    renderTabItems('General')
                  )}
                </div>
                <div
                  className={classNames(classes.hidden, {
                    [classes.open]: feedbackComplexType === 'Technical',
                  })}
                >
                  <div className={classes.complexSectionHeader}>
                    <p className={classes.complexSectionContentItemTitle}>
                      {t('common.FeedbackComplexTypeOptions.Technical')}
                    </p>

                    <Switch
                      label={t('common.sectioNATitle')}
                      isEnabled={areAllItemsNAInTab('Technical')}
                      readOnly={
                        existingFeedback !== null &&
                        (!isEditing || isReviewing || readOnly)
                      }
                      onClick={() => toggleSectionNA('Technical')}
                    />
                  </div>
                  {areAllItemsNAInTab('Technical') ? (
                    <div className={classes.sectionNA}>
                      {t('common.sectionNA')}
                    </div>
                  ) : (
                    renderTabItems('Technical')
                  )}
                </div>
                <div
                  className={classNames(classes.hidden, {
                    [classes.open]: feedbackComplexType === 'Engagement',
                  })}
                  hidden={feedbackComplexType !== 'Engagement'}
                >
                  <div className={classes.complexSectionHeader}>
                    <p className={classes.complexSectionContentItemTitle}>
                      {t('common.FeedbackComplexTypeOptions.Engagement')}
                    </p>

                    <Switch
                      label={t('common.sectioNATitle')}
                      isEnabled={areAllItemsNAInTab('Engagement')}
                      readOnly={
                        existingFeedback !== null &&
                        (!isEditing || isReviewing || readOnly)
                      }
                      onClick={() => toggleSectionNA('Engagement')}
                    />
                  </div>
                  {areAllItemsNAInTab('Engagement') ? (
                    <div className={classes.sectionNA}>
                      {t('common.sectionNA')}
                    </div>
                  ) : (
                    renderTabItems('Engagement')
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
